import tinycolor from 'tinycolor2';

// cspell:ignore RRGGBB RRGGBBAA

const parseColorToRgba = (color: string) => tinycolor(color).toRgb();

/**
 * 将十六进制颜色转换为 0-1 范围的 RGBA 通道。
 *
 * @param color - 十六进制颜色值。
 * @returns 0-1 范围的 RGBA 通道。
 */
export const hexToNormalizedRgba = (color: string) => {
    const { r, g, b, a } = parseColorToRgba(color);
    return { r: r / 255, g: g / 255, b: b / 255, a };
};

/**
 * 将 CSS 颜色转换为按 RGBA 通道打包的无符号 32 位整数。
 *
 * @param color - CSS 颜色字符串。
 * @returns 按 RGBA 通道打包的无符号 32 位整数。
 */
export const colorToRgbaUint32 = (color: string): number => {
    const { r, g, b, a } = parseColorToRgba(color);
    return (((r & 0xff) << 24) | ((g & 0xff) << 16) | ((b & 0xff) << 8) | (Math.round(a * 255) & 0xff)) >>> 0;
};

/**
 * 将按 RGBA 通道打包的无符号 32 位整数转换为 CSS 颜色。
 *
 * @param rgba - 按 R/G/B/A 顺序编码的无符号整数。
 * @returns CSS rgba 颜色字符串。
 */
export const rgbaUint32ToCss = (rgba: number): string => {
    const red = (rgba >>> 24) & 0xff;
    const green = (rgba >>> 16) & 0xff;
    const blue = (rgba >>> 8) & 0xff;
    const alpha = rgba & 0xff;
    return `rgba(${red}, ${green}, ${blue}, ${Number((alpha / 255).toFixed(4))})`;
};

/**
 * 拆分普通十六进制颜色及其 alpha 通道。
 *
 * @param input - #RRGGBB 或 #RRGGBBAA 颜色。
 * @returns 不透明颜色与 0-1 opacity。
 */
export const splitHexAlpha = (input: string): { color: string; opacity: number } => {
    const match = input.trim().match(/^#([0-9a-fA-F]{6})([0-9a-fA-F]{2})?$/);
    if (!match) return { color: input, opacity: 1 };
    return {
        color: `#${match[1].toLowerCase()}`,
        opacity: match[2] ? parseInt(match[2], 16) / 255 : 1,
    };
};

/**
 * 十六进制颜色值转 RGBA 颜色值数组
 *
 * @param sHex - 十六进制颜色值
 * @returns 返回 RGBA 格式的颜色值
 */
export const hexToRgbaArr = (sHex: string) => {
    // 十六进制颜色值的正则表达式
    const reg = /^#([0-9a-fA-f]{3}|[0-9a-fA-f]{4}|[0-9a-fA-f]{6}|[0-9a-fA-f]{8})$/;
    /* 16进制颜色转为RGB格式 */
    let sColor = sHex.toLowerCase();
    let alpha = 1;
    if (sColor && reg.test(sColor)) {
        if (sColor.length === 4 || sColor.length === 5) {
            let sColorNew = '#';
            for (let i = 1; i < sColor.length; i += 1) {
                sColorNew += sColor.slice(i, i + 1).concat(sColor.slice(i, i + 1));
            }
            sColor = sColorNew;
        }
        // 如果有透明度再执行
        if (sColor.length === 9) {
            alpha = Number((parseInt('0x' + sColor.slice(7, 9)) / 255).toFixed(2));
        }
        //  处理六位的颜色值
        const sColorChange: number[] = [];
        for (let i = 1; i < 7; i += 2) {
            sColorChange.push(parseInt('0x' + sColor.slice(i, i + 2)));
        }
        return [...sColorChange, alpha];
    } else {
        return sColor;
    }
};

/**
 * 检查颜色值是否为线性渐变
 *
 * @param color - 颜色值
 * @returns 返回是否为线性渐变
 */
export const checkLinearGradient = function (color: string) {
    return color.indexOf('linear-gradient') !== -1;
};

/**
 * 判断颜色是否为暗色
 * @doc https://cf.meitu.com/confluence/pages/viewpage.action?pageId=531357711
 * @param bgColor - 颜色
 * @returns
 */
export const checkIsDarkColor = (color: string) => {
    try {
        if (!color || color === 'transparent') return false;
        const rgba = parseColorToRgba(color);
        // 计算相对亮度
        const luminance = (0.299 * rgba.r + 0.587 * rgba.g + 0.114 * rgba.b) / 255;
        // 返回是否为暗色
        return luminance < 0.5;
    } catch (_) {
        return false;
    }
};

/**
 * 检查颜色是否为透明色
 *
 * @param color - 颜色值
 * @returns 返回是否为透明色
 */
export const checkIsTransparentColor = (color?: string) => {
    if (!color) {
        return false;
    }

    if (color === 'transparent') {
        return true;
    }

    const rgba = parseColorToRgba(color);
    return rgba.a === 0;
};
